// Lazy-initialised Firebase web app. We only spin up the SDK the first time
// the phone-auth flow needs it, keeping the bundle out of the critical path
// for the public marketing pages.
//
// NOTE: A known-benign reCAPTCHA cleanup error ("Invalid reCAPTCHA client
// id: xxxxx") is suppressed globally via
// `src/lib/recaptcha-noise-suppressor.js`, imported at the very top of
// `index.js` so it hooks into `window.onerror` before React does.
import { initializeApp, getApps } from "firebase/app";
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
};

/** Has the developer plugged in Firebase web config? */
export function isFirebaseConfigured() {
  return Boolean(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId &&
    firebaseConfig.appId
  );
}

let _app = null;
let _auth = null;

export function getFirebaseAuth() {
  if (!isFirebaseConfigured()) return null;
  if (!_app) {
    _app = getApps()[0] || initializeApp(firebaseConfig);
  }
  if (!_auth) {
    _auth = getAuth(_app);
    // Firebase warns if the device language isn't set — surface the browser's.
    if (typeof navigator !== "undefined") {
      _auth.useDeviceLanguage();
    }
    // Test-mode bypass — when enabled, Firebase skips the reCAPTCHA challenge
    // and only honours whitelisted "Test phone numbers" set in the Firebase
    // Console. Real phone numbers will not work in this mode. Toggle off in
    // production once reCAPTCHA Enterprise / App Check is configured.
    if (isFirebaseTestMode()) {
      _auth.settings.appVerificationDisabledForTesting = true;
    }
  }
  return _auth;
}

/**
 * Whether to disable the live reCAPTCHA challenge (test-phone flow only).
 * Driven by REACT_APP_FIREBASE_TEST_MODE — defaults to "true" so the preview
 * domain works out of the box with the seeded test phone numbers.
 */
export function isFirebaseTestMode() {
  const v = process.env.REACT_APP_FIREBASE_TEST_MODE;
  // Treat unset / "true" / "1" as enabled. Only explicit "false" / "0" disables.
  if (v == null || v === "") return true;
  return !["false", "0", "off", "no"].includes(String(v).toLowerCase());
}

/**
 * Ensure the reCAPTCHA container is *empty* before instantiating a new
 * verifier — Firebase's `validateStartingState()` throws `auth/argument-error`
 * if it finds child nodes left from a previous mount/render.
 */
function ensureEmptyContainer(containerId) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(containerId);
  if (el && el.hasChildNodes()) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }
}

/**
 * Create (or reuse) an invisible reCAPTCHA verifier rendered inside the
 * given container id. Returns null if Firebase isn't configured.
 */
export function getRecaptchaVerifier(containerId) {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  // Re-use the global one if it already exists — Firebase complains otherwise.
  if (window.__psg_recaptcha_verifier) return window.__psg_recaptcha_verifier;

  ensureEmptyContainer(containerId);

  // Firebase v10+ modular signature: (authExtern, containerOrId, parameters)
  const verifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
    callback: () => {},
    "expired-callback": () => { resetRecaptchaVerifier(); },
  });
  window.__psg_recaptcha_verifier = verifier;
  return verifier;
}

/** Reset the recaptcha (call after failed verify or when the dialog closes). */
export function resetRecaptchaVerifier() {
  if (window.__psg_recaptcha_verifier) {
    try {
      window.__psg_recaptcha_verifier.clear();
    } catch (_e) { /* ignore */ }
  }
  window.__psg_recaptcha_verifier = null;
  window.__psg_recaptcha_widget_id = null;
}

/** Send an OTP to `+91xxxxxxxxxx` (already formatted). Returns confirmationResult. */
export async function sendPhoneOtp(e164Phone, containerId) {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase auth not configured");
  const verifier = getRecaptchaVerifier(containerId);
  try {
    return await signInWithPhoneNumber(auth, e164Phone, verifier);
  } catch (err) {
    // If the verifier got into a bad state, drop it so the next call rebuilds.
    resetRecaptchaVerifier();
    throw err;
  }
}
